import { Validators } from '@biteinc/common';
import { allEnumValues, DiscountType, FulfillmentMethod, GroupOrderingMode, IntegrationSystem, KioskOs, LanguageCode, OrderPaymentDestination, OrderState, RewardType, RewardApiVersion, OrderPrintState, ServiceChargeSource, } from '@biteinc/enums';
const loyaltyRewardV2Fields = {
    rewardApiVersion: {
        type: 'stringEnum',
        enum: RewardApiVersion,
    },
    name: {
        type: 'shortString',
    },
    description: {
        type: 'shortString',
    },
    localizationNameKey: {
        type: 'keyString',
    },
    localizationNameWithAmountKey: {
        type: 'keyString',
    },
    localizationDescriptionKey: {
        type: 'keyString',
    },
    type: {
        type: 'int',
        values: allEnumValues(RewardType),
    },
    amount: {
        type: 'price',
    },
    canAutoApply: {
        type: 'bool',
    },
    rewardI9nId: {
        type: 'keyString',
    },
    pointsUser: {
        type: 'int',
    },
    pointsUsed: {
        type: 'int',
    },
    pointsCode: {
        type: 'keyString',
    },
    itemI9nId: {
        type: 'keyString',
    },
    quantityOfItemToDiscount: {
        type: 'int',
    },
    i9nType: {
        type: 'keyString',
    },
    i9nId: {
        type: 'keyString',
    },
    shouldBeHidden: {
        type: 'bool',
    },
    originalRewardI9nId: {
        type: 'keyString',
    },
    rewardI9nItems: {
        type: 'array',
        elementType: 'object',
        isFreeform: true,
    },
    notCurrentlyRedeemable: {
        type: 'bool',
    },
    isApplicable: {
        type: 'bool',
    },
    imageUrl: {
        type: 'url',
    },
    transactionId: {
        type: 'mongoId',
    },
    serializedI9nReward: {
        type: 'keyString',
    },
    isRewardCode: {
        type: 'bool',
    },
    metadata: {
        type: 'object',
        isFreeform: true,
    },
};
const appliedCouponFields = {
    integrationId: {
        type: 'mongoId',
    },
    i9nId: {
        type: 'keyString',
    },
    name: {
        type: 'keyString',
    },
    type: {
        type: 'int',
        values: allEnumValues(RewardType),
    },
    amount: {
        type: 'price',
    },
    couponAppliedAt: {
        type: 'timestamp',
    },
    calculatedAmount: {
        type: 'price',
    },
};
const integrationFields = {
    _id: {
        type: 'mongoId',
        required: true,
    },
    integrationId: {
        type: 'mongoId',
    },
    vendorId: {
        type: 'mongoId',
        required: true,
    },
    system: {
        type: 'int',
        values: allEnumValues(IntegrationSystem),
        required: true,
    },
    subTotal: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    taxTotal: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    discountTotal: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    tipTotal: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    serviceChargeTotal: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    donationTotal: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    total: {
        type: 'price',
        // This field will only be set for POS integrations
    },
    validatedAt: {
        type: 'timestamp',
    },
    validationWasSkipped: {
        type: 'keyString',
    },
    validationData: {
        type: 'object',
        isFreeform: true,
    },
    createdOnPosAt: {
        type: 'timestamp',
    },
    creationData: {
        type: 'object',
        isFreeform: true,
    },
    creationWasSkipped: {
        type: 'keyString',
    },
    sentAt: {
        type: 'timestamp',
    },
    sendingWasSkipped: {
        type: 'keyString',
    },
    sendData: {
        type: 'object',
        isFreeform: true,
    },
    updateData: {
        type: 'object',
        isFreeform: true,
    },
    updateWasSkipped: {
        type: 'keyString',
    },
    voidedAt: {
        type: 'timestamp',
    },
    voidingWasSkipped: {
        type: 'keyString',
    },
    rewardsAppliedAt: {
        type: 'timestamp',
    },
    rewards: {
        type: 'array',
        elementType: 'object',
        fields: {
            ...loyaltyRewardV2Fields,
            posDiscountId: {
                type: 'keyString',
            },
        },
    },
    fetchedRewards: {
        type: 'array',
        elementType: 'object',
        fields: {
            ...loyaltyRewardV2Fields,
            posDiscountId: {
                type: 'keyString',
            },
        },
    },
    fetchedRewardsHash: { type: 'keyString' },
    loyaltyToken: { type: 'keyString' },
    loyaltyGuestData: {
        type: 'object',
        fields: {
            guestEmail: { type: 'email' },
            guestPhoneNumber: { type: 'phone' },
            guestName: { type: 'keyString' },
            statusDescription: { type: 'shortString' },
        },
    },
    pointsBalance: { type: 'int' },
    transactionI9nId: { type: 'mongoId' },
    compCardToken: { type: 'keyString' },
    updatedOnPosAt: { type: 'timestamp' },
    storedValueAuthDataToken: { type: 'keyString' },
    storedValueRemainingBalance: { type: 'price' },
    storedValueAuthData: { type: 'object', isFreeform: true },
    sendAttempts: {
        type: 'array',
        elementType: 'object',
        fields: {
            startedAt: {
                type: 'timestamp',
            },
            endedAt: {
                type: 'timestamp',
            },
            error: {
                type: 'object',
                isFreeform: true,
            },
            isFinalAttempt: {
                type: 'bool',
            },
        },
    },
    autoAppliedDiscountIds: {
        type: 'array',
        elementType: 'keyString',
    },
    discounts: {
        type: 'array',
        elementType: 'object',
        fields: {
            i9nId: {
                type: 'keyString',
                isPermanent: true,
            },
            name: {
                type: 'keyString',
                isPermanent: true,
            },
            type: {
                type: 'int',
                values: allEnumValues(DiscountType),
            },
            amount: {
                type: 'price',
                isPermanent: true,
            },
            posDiscountId: {
                type: 'keyString',
                isPermanent: true,
            },
            itemIds: {
                type: 'array',
                elementType: 'mongoId',
                isPermanent: true,
            },
            i9nData: {
                type: 'object',
                isPermanent: true,
                isFreeform: true,
            },
        },
    },
    serviceCharges: {
        type: 'array',
        elementType: 'object',
        fields: {
            name: {
                type: 'keyString',
                isPermanent: true,
            },
            amount: {
                type: 'price',
                isPermanent: true,
            },
            source: {
                type: 'stringEnum',
                enum: ServiceChargeSource,
                isPermanent: true,
            },
            transactionId: {
                type: 'mongoId',
                isPermanent: true,
                conditions: [{ field: 'source', values: [ServiceChargeSource.Payment] }],
            },
        },
    },
    taxes: {
        type: 'array',
        elementType: 'object',
        fields: {
            i9nId: {
                type: 'keyString',
                isPermanent: true,
            },
            name: {
                type: 'keyString',
                isPermanent: true,
            },
            amount: {
                type: 'price',
                isPermanent: true,
            },
        },
    },
};
/**
 * A writeable version of the order schema. Used by the push API used by offline mode.
 */
export const orderSchemaWriteable = {
    displayName: 'order',
    type: 'object',
    fields: {
        _id: {
            type: 'mongoId',
            required: true,
        },
        createdAt: {
            type: 'timestamp',
            required: true,
        },
        updatedAt: {
            type: 'timestamp',
            required: true,
        },
        clientId: {
            type: 'mongoId',
            required: true,
            isPermanent: true,
        },
        userId: {
            type: 'mongoId',
        },
        customerId: {
            type: 'mongoId',
        },
        recommendationVariationId: {
            type: 'mongoId',
        },
        clientVersion: {
            type: 'int',
        },
        loyaltyAuthData: {
            type: 'object',
            isFreeform: true,
        },
        clientNumber: {
            type: 'keyString',
            isPermanent: true,
        },
        gcnBuildName: { type: 'keyString' },
        garconBuildName: { type: 'keyString' },
        vitrineBuildName: { type: 'keyString' },
        kioskBuild: { type: 'int' },
        kioskOs: { type: 'stringEnum', enum: KioskOs },
        kioskOsVersion: { type: 'keyString' },
        kioskName: { type: 'keyString' },
        userAgent: { type: 'keyString' },
        referer: { type: 'keyString' },
        sequenceNumber: {
            type: 'int',
            isPermanent: true,
        },
        includeUtensils: {
            type: 'bool',
        },
        clientState: {
            type: 'int',
        },
        isKioskPreview: {
            type: 'bool',
        },
        isDemo: {
            type: 'bool',
        },
        orderedItemsLeadTime: {
            type: 'int',
        },
        originalSubTotal: {
            type: 'price',
        },
        startedAt: {
            type: 'timestamp',
        },
        sessionId: {
            type: 'keyString',
        },
        groupOrderingMode: {
            type: 'stringEnum',
            enum: GroupOrderingMode,
        },
        preTaxExemptTaxTotal: {
            type: 'price',
        },
        userDidRejectConsent: {
            type: 'bool',
        },
        guestSurveyMaximum: {
            type: 'int',
        },
        wasValidated: {
            type: 'timestamp',
        },
        validationWasRequestedAt: { type: 'timestamp' },
        tipWasSetByGuest: { type: 'bool' },
        validationWasSkipped: {
            type: 'keyString',
            isPermanent: true,
        },
        pickupAt: {
            type: 'timestamp',
        },
        wasSentToPos: {
            type: 'timestamp',
            isPermanent: true,
        },
        wasSentForFulfillment: {
            type: 'bool',
            isPermanent: true,
        },
        sendingToPosWasSkipped: {
            type: 'keyString',
            isPermanent: true,
        },
        guestId: {
            type: 'mongoId',
        },
        guestConsentedToMarketing: {
            type: 'bool',
        },
        channel: {
            type: 'keyString',
        },
        paymentDestination: {
            type: 'int',
            values: allEnumValues(OrderPaymentDestination),
        },
        fulfillmentMethod: {
            type: 'int',
            values: allEnumValues(FulfillmentMethod),
            required: true,
        },
        menuStructureId: {
            type: 'mongoId',
        },
        outpostId: {
            type: 'mongoId',
        },
        tableNumber: {
            type: 'keyString',
            validationRegex: Validators.simpleStringRegexString({ rejectEmptySpace: true }),
        },
        guestName: {
            type: 'keyString',
            validationRegex: Validators.simpleStringRegexString({ rejectEmptySpace: true }),
        },
        guestPhoneNumber: {
            type: 'phone',
        },
        guestEmail: {
            type: 'email',
        },
        receiptSentAt: {
            type: 'timestamp',
        },
        receiptSendError: {
            type: 'object',
            isFreeform: true,
        },
        outpost: {
            type: 'object',
            fields: {
                _id: {
                    type: 'mongoId',
                },
                name: {
                    type: 'keyString',
                },
                pickupInstructions: {
                    type: 'keyString',
                },
                instructionalImageUrl: {
                    type: 'url',
                },
                roomNumber: {
                    type: 'keyString',
                },
            },
        },
        hasLoyaltyProgram: {
            type: 'bool',
        },
        subTotal: {
            type: 'price',
            required: true,
        },
        taxTotal: {
            type: 'price',
        },
        discountTotal: {
            type: 'price',
        },
        tipTotal: {
            type: 'price',
        },
        serviceChargeTotal: {
            type: 'price',
        },
        donationTotal: {
            type: 'price',
        },
        total: {
            type: 'price',
        },
        isTaxExempt: {
            type: 'bool',
        },
        orderedItems: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'OrderedItemDb',
        },
        clientOrderedItems: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'OrderedItemDb',
        },
        refunds: {
            type: 'array',
            elementType: 'object',
            fields: {
                _id: {
                    type: 'mongoId',
                },
                accessUserId: {
                    type: 'mongoId',
                },
                transactionId: {
                    type: 'mongoId',
                },
                refundTransactionId: {
                    type: 'mongoId',
                },
                state: {
                    type: 'int',
                },
                subTotal: {
                    type: 'price',
                },
                taxTotal: {
                    type: 'price',
                },
                total: {
                    type: 'price',
                },
                discountTotal: {
                    type: 'price',
                },
                tipTotal: {
                    type: 'price',
                },
                donationTotal: {
                    type: 'price',
                },
                serviceChargeTotal: {
                    type: 'price',
                },
                accessUsername: {
                    type: 'keyString',
                },
                createdAt: {
                    type: 'timestamp',
                },
            },
        },
        appliedRewards: {
            type: 'array',
            elementType: 'object',
            fields: {
                ...loyaltyRewardV2Fields,
                posIntegrationId: {
                    type: 'mongoId',
                },
                integrationId: {
                    type: 'mongoId',
                },
                rewardAppliedAt: {
                    type: 'timestamp',
                },
            },
        },
        appliedCompCards: {
            type: 'array',
            elementType: 'object',
            fields: {
                posIntegrationId: {
                    type: 'mongoId',
                },
                integrationId: {
                    type: 'mongoId',
                },
                rewardApiVersion: {
                    type: 'keyString',
                },
                i9nId: {
                    type: 'keyString',
                },
                name: {
                    type: 'keyString',
                },
                amount: {
                    type: 'price',
                },
                type: {
                    type: 'int',
                    values: allEnumValues(RewardType),
                },
                i9nItems: {
                    type: 'array',
                    elementType: 'object',
                    fields: {
                        i9nId: {
                            type: 'keyString',
                        },
                    },
                },
                rewardAppliedAt: {
                    type: 'timestamp',
                },
                system: {
                    type: 'int',
                    values: allEnumValues(IntegrationSystem),
                },
            },
        },
        appliedPosCoupons: {
            type: 'array',
            elementType: 'object',
            fields: {
                integrationId: {
                    type: 'mongoId',
                },
                itemId: {
                    type: 'mongoId',
                },
                itemIds: {
                    type: 'array',
                    elementType: 'mongoId',
                },
                modifierIds: {
                    type: 'array',
                    elementType: 'mongoId',
                },
                i9nId: {
                    type: 'keyString',
                },
                name: {
                    type: 'keyString',
                },
                type: {
                    type: 'int',
                    values: allEnumValues(RewardType),
                },
                amount: {
                    type: 'price',
                },
                couponAppliedAt: {
                    type: 'timestamp',
                },
                calculatedAmount: {
                    type: 'price',
                },
                posDiscountId: {
                    type: 'keyString',
                },
                itemI9nIds: {
                    type: 'array',
                    elementType: 'keyString',
                },
            },
        },
        appliedBiteCoupons: {
            type: 'array',
            elementType: 'object',
            fields: {
                ...appliedCouponFields,
                managedCouponId: {
                    type: 'mongoId',
                },
                itemId: {
                    type: 'mongoId',
                },
                posDiscountId: {
                    type: 'keyString',
                },
                calculatedRate: {
                    type: 'float',
                },
                singleUseCouponCode: {
                    type: 'keyString',
                },
                posDiscountIsOpen: {
                    type: 'bool',
                },
                retireAtOrgLevel: {
                    type: 'bool',
                },
            },
        },
        appliedLoyaltyCoupons: {
            type: 'array',
            elementType: 'object',
            fields: {
                ...appliedCouponFields,
                i9nType: {
                    type: 'keyString',
                },
                rewardI9nId: {
                    type: 'keyString',
                },
            },
        },
        itemPriceCorrectionById: {
            type: 'map',
            key: {
                type: 'mongoId',
            },
            fields: {
                unitPrice: {
                    type: 'price',
                },
                name: {
                    type: 'shortString',
                },
            },
        },
        itemComboCorrectionById: {
            type: 'map',
            key: {
                type: 'mongoId',
            },
            fields: {
                comboI9nId: {
                    type: 'keyString',
                },
                comboName: {
                    type: 'shortString',
                },
                comboPrice: {
                    type: 'price',
                },
            },
        },
        loyaltyOrderIds: {
            type: 'array',
            elementType: 'keyString',
        },
        fulfillmentOrderIds: {
            type: 'array',
            elementType: 'keyString',
        },
        unappliedRewards: {
            type: 'array',
            elementType: 'object',
            fields: loyaltyRewardV2Fields,
        },
        recommendationExperimentConfiguration: {
            type: 'object',
            isFreeform: true,
        },
        expoPrintState: {
            type: 'int',
            values: allEnumValues(OrderPrintState),
        },
        expoPrintRequestedAt: { type: 'timestamp' },
        numberOfFailedLoyaltySignInAttempts: {
            type: 'int',
        },
        numberOfFailedGiftCardAccessAttempts: {
            type: 'int',
        },
        serviceCharges: {
            type: 'array',
            elementType: 'object',
            fields: {
                name: {
                    type: 'keyString',
                    isPermanent: true,
                },
                amount: {
                    type: 'price',
                    isPermanent: true,
                },
                source: {
                    type: 'stringEnum',
                    enum: ServiceChargeSource,
                    isPermanent: true,
                },
                transactionId: {
                    type: 'mongoId',
                    isPermanent: true,
                    conditions: [{ field: 'source', values: [ServiceChargeSource.Payment] }],
                },
            },
        },
        validatedDiscountLabels: {
            type: 'array',
            elementType: 'object',
            fields: {
                name: {
                    type: 'keyString',
                    isPermanent: true,
                },
                localizationNameKey: {
                    type: 'keyString',
                    isPermanent: true,
                },
                type: {
                    type: 'int',
                    values: allEnumValues(DiscountType),
                },
                amount: {
                    type: 'price',
                    isPermanent: true,
                },
                i9nId: {
                    type: 'keyString',
                    isPermanent: true,
                },
                itemIds: {
                    type: 'array',
                    elementType: 'mongoId',
                    isPermanent: true,
                },
                amountByOrderedItemId: {
                    type: 'object',
                    isPermanent: true,
                    isFreeform: true,
                },
            },
        },
        integrationById: {
            type: 'map',
            key: {
                type: 'mongoId',
            },
            fields: integrationFields,
        },
        dataForVendors: {
            type: 'array',
            elementType: 'object',
            fields: integrationFields,
        },
        guestSurveyValue: {
            type: 'int',
            min: 0,
            max: 3,
        },
        receiptShortCode: {
            type: 'keyString',
        },
        lockExpiresAt: {
            type: 'timestamp',
        },
        lockLeaseId: {
            type: 'keyString',
        },
        lockDomain: {
            type: 'keyString',
        },
        isClosed: {
            type: 'bool',
        },
        closedAt: {
            type: 'timestamp',
        },
        isBiteFutureOrder: {
            type: 'bool',
        },
        isI9nFutureOrder: {
            type: 'bool',
        },
        ordersApiVersion: {
            type: 'keyString',
        },
        posOrderIds: {
            type: 'array',
            elementType: 'keyString',
        },
        originEnvironment: {
            type: 'keyString',
        },
        isCancelled: {
            type: 'bool',
        },
        abandonedAt: {
            type: 'timestamp',
        },
        addedSendOrderJobAt: {
            type: 'timestamp',
        },
        selectedLanguage: {
            type: 'stringEnum',
            enum: LanguageCode,
        },
        hasTransactions: {
            type: 'bool',
        },
        transactionIds: {
            type: 'array',
            elementType: 'mongoId',
        },
        isClosedOrIsFinished: {
            type: 'bool',
        },
        hasFailedToSendToPos: {
            type: 'bool',
        },
        state: {
            type: 'stringEnum',
            enum: OrderState,
        },
    },
};
//# sourceMappingURL=order_schema_writeable.js.map